import {
  HStack,
  VStack,
  IconButton,
  Box,
  Text,
  Image,
  useDisclosure,
} from "@chakra-ui/react";

import { Key, useEffect, useMemo, useRef, useState } from "react";
import { HiDownload } from "react-icons/hi";
import { FaFileAlt } from "react-icons/fa";
import { OrderStatus, Roles, fileDownloadImage } from "../enums";
import GaraazPartConversion from "./GaraazPartConversion";
import GMainImageComponent from "./GaraazZoomer";
import ImageModal from "./ImageModal";
import RemarksModel from "./RemarksModel";
import { AllocatedTo, finalOrcaStates } from "./GaraazOrderComponent";
import { useOrdersStore } from "../store/useOrdersStore";
import { GaraazTextComponent } from "@svan-garaaz/reactsharedlib";
import GaraazVinLpnEditor from "./GaraazVinLpnEditor";
import { motion } from "framer-motion";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import AudioThumbnail from "./AudioThumbnail";
import AudioPlayerComponent from "./AudioPlayerComponent";
import { normalizeMultilineText } from "../../../utils/textUtils";
import { useOrderLabel } from "./GaraazOrderCard/OrderLabelContext";

type GaraazOrderComponentBodyProps = {
  orderStatus: any;
  orderData: any;
  displayPartConversion: string;
  findBrandName: (brandId: string) => any;
  activesupplierOrderStatus: AllocatedTo;
  getSupplierBusinessName: (supplierId: string) => any;
  garaazRemarks: string;
  isEnquiryLocked?: boolean;
  isPartsFreezed?: boolean;
  refetchAllOrders?: () => void;
};

const fadeInOut = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const GaraazOrderComponentBody: React.FC<GaraazOrderComponentBodyProps> = ({
  orderData,
  orderStatus,
  displayPartConversion,
  findBrandName,
  activesupplierOrderStatus,
  getSupplierBusinessName,
  garaazRemarks,
  isEnquiryLocked = false,
  isPartsFreezed = false,
  refetchAllOrders,
}) => {
  const { isEnquiryOrder } = useOrderLabel();

  // Audio detection and state management - must be before mainImage initialization
  const audioFiles = useMemo(() => {
    return (
      orderData?.orderMedia?.filter((item: any) =>
        item?.mimeType?.startsWith("audio")
      ) || []
    );
  }, [orderData?.orderMedia]);

  const hasAudioFiles = audioFiles.length > 0;

  // Find first non-audio media for initial display
  const firstNonAudioMedia = useMemo(() => {
    return orderData?.orderMedia?.find(
      (item: any) => !item?.mimeType?.startsWith("audio")
    );
  }, [orderData?.orderMedia]);

  // Initialize mainImage to empty if audio exists, otherwise first non-audio media
  const [mainImage, setMainImage] = useState(
    hasAudioFiles ? {} : firstNonAudioMedia || {}
  );

  // Show audio player by default if there are audio files
  const [showAudioPlayer, setShowAudioPlayer] = useState(hasAudioFiles);

  const [isOpen, setIsOpen] = useState(false);
  const savedOrderRemarks = useOrdersStore(
    (state) => state.orderRemarks[orderData._id]
  );

  const audioPlayerRef = useRef<{ stopAllAudio: () => void } | null>(null);

  const [vinNumber, setVinNumber] = useState(orderData?.vinNumber || "");
  const [licensePlateNumber, setLicensePlateNumber] = useState(
    orderData?.licensePlateNumber || ""
  );
  const [isVinVerified, setIsVinVerified] = useState(
    orderData?.isVinNumberVerified
  );
  const [isLpnVerified, setIsLpnVerified] = useState(
    orderData?.isLicencePlateNumberVerified
  );

  const [showVinEditor, setShowVinEditor] = useState(
    !orderData?.isVinNumberVerified
  );
  const [showLpnEditor, setShowLpnEditor] = useState(
    !orderData?.isLicencePlateNumberVerified
  );
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const userDetails = useUserDetailsContext();

  const {
    isOpen: isRemarksModalOpen,
    onOpen: onRemarksModalOpen,
    onClose: onRemarksModalClose,
  } = useDisclosure();
  const {
    isOpen: isSupplierRemarksModalOpen,
    onOpen: onSupplierRemarksModalOpen,
    onClose: onSupplierRemarksModalClose,
  } = useDisclosure();
  const {
    isOpen: isOrderRemarksModalOpen,
    onOpen: onOrderRemarksModalOpen,
    onClose: onOrderRemarksModalClose,
  } = useDisclosure();

  useEffect(() => {
    // If audio files exist, show audio player by default
    if (hasAudioFiles) {
      setMainImage({});
      setShowAudioPlayer(true);
    } else {
      // No audio - show first non-audio media
      const firstNonAudio = orderData?.orderMedia?.find(
        (item: any) => !item?.mimeType?.startsWith("audio")
      );
      setMainImage(firstNonAudio || {});
      setShowAudioPlayer(false);
    }
  }, [orderData.orderMedia, hasAudioFiles]);

  useEffect(() => {
    setVinNumber(orderData?.vinNumber || "");
    setLicensePlateNumber(orderData?.licensePlateNumber || "");
    setIsVinVerified(orderData?.isVinNumberVerified);
    setIsLpnVerified(orderData?.isLicencePlateNumberVerified);
  }, [orderData]);

  function handleClickVerticalImages(Image: string | undefined) {
    setMainImage(Image || {});
  }
  const handleMediaHover = (item: any) => {
    if (
      !showAudioPlayer &&
      (mainImage?._id
        ? mainImage?._id === item?._id
        : mainImage?.path === item?.path)
    ) {
      return;
    }
    handleClickVerticalImages(item);
    setShowAudioPlayer(false);
    audioPlayerRef.current?.stopAllAudio();
  };

  const handleAudioHover = () => {
    if (showAudioPlayer) {
      return;
    }
    setShowAudioPlayer(true);
    setMainImage({});
  };

  function handleModalOpen() {
    const previousImage = mainImage;
    setMainImage({
      _id: "",
      userId: "",
      path: "",
      mimeType: previousImage.mimeType,
      __v: 0,
      createdAt: "",
      updatedAt: "",
    });
    setTimeout(() => {
      setMainImage(previousImage);
    }, 1);
    setIsOpen(true);
  }

  const isPCFreezedPostPCEnd =
    mobileConfigs?.ff_freeze_order_items_post_part_conversion === "true" &&
    !!orderData?.metaInfo?.partConversionEndTime;

  const freezeAfterPC = isEnquiryOrder
    ? isEnquiryLocked
    : isPCFreezedPostPCEnd;

  const isDisabled =
    orderData.orderType === "RETURN" ||
    orderData.orderType === "SUBSCRIPTION" ||
    finalOrcaStates.includes(orderStatus) ||
    orderStatus === OrderStatus.READY_TO_DISPATCH ||
    orderStatus === OrderStatus.DELIVERED ||
    !orderData?.metaInfo?.partConversionStartTime ||
    freezeAfterPC;

  const isRenderableImage = (item: any) =>
    Boolean(item?.mimeType?.startsWith("image")) && item?.path !== fileDownloadImage;

  return (
    <HStack
      width={"100%"}
      flex={1}
      minH={0}
      height="auto"
      marginTop={"0"}
      style={{ marginTop: "0" }}
      padding={"10px"}
      justifyContent={"space-evenly"}
      bgColor={"white"}
      overflowX="hidden"
      overflowY="auto"
      alignItems="stretch"
    >
      <HStack
        width={"100%"}
        height={"100%"}
        display={`${displayPartConversion}`}
      >
        {orderData?.orderMedia?.length > 0 && isEnquiryOrder && (
          <Box width={"8%"} position={"relative"} height={"100%"}>
            <VStack
              height={"100%"}
              width={"100%"}
              overflow={"scroll"}
              position={"absolute"}
            >
              {hasAudioFiles && (
                <AudioThumbnail
                  onMouseEnter={handleAudioHover}
                  isActive={showAudioPlayer}
                />
              )}
              {orderData?.orderMedia?.map(
                (item: any, index: Key | null | undefined) => {
                  // Skip audio files - they're represented by the AudioThumbnail
                  if (item?.mimeType?.startsWith("audio")) {
                    return null;
                  }

                  return isRenderableImage(item) ? (
                    <Image
                      key={index}
                      src={item.path}
                      height={"60px"}
                      width={"100%"}
                      objectFit={"cover"}
                      border={"3px solid lightgrey"}
                      onMouseEnter={() => handleMediaHover(item)}
                    />
                  ) : (
                    <Box
                      key={index}
                      height={"60px"}
                      width={"100%"}
                      border={"3px solid lightgrey"}
                      bg={"gray.50"}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      onMouseEnter={() => handleMediaHover(item)}
                    >
                      <FaFileAlt size={24} color="#4A5568" />
                    </Box>
                  );
                }
              )}
            </VStack>
          </Box>
        )}

        {orderData?.orderMedia && orderData?.orderMedia?.length > 0 && isEnquiryOrder && (
          <Box height={"100%"} width={"40%"} position={"relative"} overflow="hidden">
            {showAudioPlayer && hasAudioFiles ? (
              <AudioPlayerComponent ref={audioPlayerRef} audioFiles={audioFiles} />
            ) : isRenderableImage(mainImage) ? (
              <GMainImageComponent
                imagePath={mainImage.path}
                handleModalOpen={handleModalOpen}
              />
            ) : (
              <Box position={"relative"} width={"100%"} height={"100%"} overflow="hidden">
                <Box
                  position="absolute"
                  inset={0}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={3}
                >
                  <Image
                    src={fileDownloadImage}
                    alt="File preview"
                    width="100%"
                    height="100%"
                    maxW="72%"
                    maxH="72%"
                    objectFit="contain"
                  />
                </Box>

                <IconButton
                  position={"absolute"}
                  top={0}
                  right={0}
                  alignSelf={"end"}
                  aria-label="Download"
                  icon={
                    <HiDownload style={{ fontSize: "1.5em", color: "black" }} />
                  }
                  size="sm"
                  onClick={() => {
                     if (!mainImage?.path) return;
                     window.open(mainImage.path, "_blank", "noopener,noreferrer");
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {orderData?.orderMedia && orderData?.orderMedia?.length > 0 && (
          <ImageModal
            mediaFiles={orderData.orderMedia}
            mainImage={mainImage}
            isOpen={isOpen}
            onClose={() => {
              setIsOpen(false);
            }}
          />
        )}

        <Box
          flex={1}
          width={"100%"}
          height={"100%"}
          style={{
            margin: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <GaraazPartConversion
            orderData={orderData}
            isDisabled={isDisabled}
            isEnquiryLocked={isEnquiryLocked}
            orderId={orderData?._id}
            findBrandName={findBrandName}
            customerGeoPoint={orderData?.shipmentAddress?.location}
            refetchAllOrders={refetchAllOrders}
            componentHeight={
              orderData?.vinNumber &&
                showVinEditor &&
                orderData?.licensePlateNumber &&
                showLpnEditor
                ? "80%"
                : (orderData?.licensePlateNumber && showLpnEditor) ||
                  (orderData?.vinNumber && showVinEditor)
                  ? "85%"
                  : "95%"
            }
            enableEnquiryPartSearchUx={isEnquiryOrder}
          ></GaraazPartConversion>
          {(showLpnEditor || showVinEditor) && (
            <Box style={{ marginTop: "auto" }}>
              {orderData?.vinNumber && showVinEditor && (
                <motion.div
                  initial="initial"
                  animate={!isVinVerified ? "animate" : "exit"}
                  exit="exit"
                  variants={fadeInOut}
                  transition={{ duration: 0.5 }}
                  onAnimationComplete={() => {
                    if (isVinVerified) {
                      setShowVinEditor(false);
                    }
                  }}
                >
                  <GaraazVinLpnEditor
                    label="vinNumber"
                    inputValue={vinNumber}
                    setInputValue={setVinNumber}
                    value={orderData?.vinNumber}
                    orderNo={orderData.orderNo}
                    isVerified={isVinVerified}
                    setIsVerified={setIsVinVerified}
                    customerRemarks={orderData?.customerRemarks}
                    orderMedia={orderData?.orderMedia}
                  />
                </motion.div>
              )}
              {orderData?.licensePlateNumber && showLpnEditor && (
                <motion.div
                  initial="initial"
                  animate={!isLpnVerified ? "animate" : "exit"}
                  exit="exit"
                  variants={fadeInOut}
                  transition={{ duration: 0.5 }}
                  onAnimationComplete={() => {
                    if (isLpnVerified) {
                      setShowLpnEditor(false);
                    }
                  }}
                >
                  <GaraazVinLpnEditor
                    label="licensePlateNumber"
                    inputValue={licensePlateNumber}
                    setInputValue={setLicensePlateNumber}
                    value={orderData?.licensePlateNumber}
                    orderNo={orderData.orderNo}
                    isVerified={isLpnVerified}
                    setIsVerified={setIsLpnVerified}
                    customerRemarks={orderData?.customerRemarks}
                    orderMedia={orderData?.orderMedia}
                  />
                </motion.div>
              )}
            </Box>
          )}
        </Box>
      </HStack>
      <Box
        width="100%"
        display={displayPartConversion === "none" ? "flex" : "none"}
        justifyContent="flex-start"
        height="100%"
        minH={0}
        overflowY="auto"
        alignItems="flex-start"
      >
        {(orderData?.customerRemarks ||
          garaazRemarks ||
          activesupplierOrderStatus?.orderRemarks) && (
            <VStack width="45%" mr={7}>
              {orderData?.customerRemarks && (
                <GaraazTextComponent
                  header="Customer Remarks"
                  content={normalizeMultilineText(orderData.customerRemarks)}
                  maxNoOfLines={5}
                  maxNoOfCharacters={100}
                />
              )}

              {garaazRemarks && (
                <GaraazTextComponent
                  header="Garaaz Remarks"
                  content={normalizeMultilineText(garaazRemarks)}
                  maxNoOfLines={2}
                  maxNoOfCharacters={100}
                />
              )}

              {activesupplierOrderStatus?.orderRemarks && (
                <GaraazTextComponent
                  header="Supplier Remarks"
                  secondaryHeader={`[${getSupplierBusinessName(
                    activesupplierOrderStatus.supplierId
                  )}]`}
                  content={normalizeMultilineText(activesupplierOrderStatus.orderRemarks)}
                  maxNoOfLines={2}
                  maxNoOfCharacters={100}
                />
              )}
            </VStack>
          )}

        <Box
          width={"50%"}
          flex={1}
          justifySelf={"flex-start"}
          marginRight={"auto"}
          marginBottom={3}
        >
          <Box onClick={onOrderRemarksModalOpen} cursor={"pointer"}>
            <Text
              color={"#0096e0"}
              fontWeight={"bold"}
              style={{ marginBottom: "auto" }}
            >
              Order Remarks
            </Text>
            {savedOrderRemarks?.slice(0, 10).map((item: any, idx: any) => {
              return (
                <Box key={idx} width={"100%"} display={"flex"}>
                  <Text style={{ fontWeight: "bold" }} whiteSpace={"nowrap"}>
                    {item.userName}:
                  </Text>
                  <Text
                    noOfLines={1}
                    color={item.userName === "SYSTEM" ? "red" : "black"}
                    ml={1}
                  >
                    {item.remarks}
                  </Text>
                </Box>
              );
            })}

            <Text style={{ lineHeight: "5px" }} fontWeight="bold">
              ...
            </Text>
            <RemarksModel
              isOpen={isOrderRemarksModalOpen}
              onClose={onOrderRemarksModalClose}
              isCustomerRemarks={false}
              modalHeader={"Order Remarks"}
              savedOrderRemarks={savedOrderRemarks}
            />
          </Box>

          <GaraazTextComponent
            header="Car Model"
            content={orderData?.carModel || "Not Provided"}
            maxNoOfLines={1}
            maxNoOfCharacters={10}
            isHorizontal={true}
            style={{ marginTop: 5, marginBottom: 4, maxWidth: "350px" }}
            copyable={!!orderData?.carModel}
          />
          <GaraazVinLpnEditor
            label="vinNumber"
            inputValue={vinNumber}
            setInputValue={setVinNumber}
            value={orderData?.vinNumber}
            orderNo={orderData.orderNo}
            isVerified={isVinVerified}
            setIsVerified={setIsVinVerified}
            customerRemarks={orderData?.customerRemarks}
            orderMedia={orderData?.orderMedia}
          />
          <GaraazVinLpnEditor
            label="licensePlateNumber"
            inputValue={licensePlateNumber}
            setInputValue={setLicensePlateNumber}
            value={orderData?.licensePlateNumber}
            orderNo={orderData.orderNo}
            isVerified={isLpnVerified}
            setIsVerified={setIsLpnVerified}
            customerRemarks={orderData?.customerRemarks}
            orderMedia={orderData?.orderMedia}
          />
        </Box>
      </Box>
    </HStack>
  );
};

export default GaraazOrderComponentBody;
